import { Pencil, Plus, RotateCw, Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Table
} from 'react-bootstrap'
import { useDebounce } from 'use-debounce'
import api from '../../../config/AxiosInstance'
import * as Common from '../../../utils/Common'
import Loader from '../../atoms/Loader'
import RowEmpty from '../../atoms/RowEmpty'
import TableAction from '../../atoms/Table/TableAction'
import TablePagination from '../../atoms/Table/TablePagination'
import ModalAddRole from '../../modal/role/ModalAddRole'
import ModalUpdateRole from '../../modal/role/ModalUpdateRole'

function MasterRolePage() {
  const headerTitleStyle = {
    color: 'var(--primary-orange)',
    fontSize: '24px',
    fontWeight: '600'
  }
  const tableHeaderStyle = {
    backgroundColor: '#f8f9fa',
    color: '#333333',
    fontWeight: '700',
    borderBottom: '1px solid #e9ecef',
    textTransform: 'uppercase',
    fontSize: '0.85rem'
  }
  const cardFilterStyle = {
    border: 'none',
    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    backgroundColor: '#fff'
  }
  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: '700',
    color: '#333333',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
    letterSpacing: '0.5px'
  }
  const FormControlStyle = {
    border: '1px solid #e9ecef',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.9rem',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s',
    height: '45px'
  }
  const tableStyle = {
    fontSize: '15px'
  }

  const [showModalAdd, setShowModalAdd] = useState(false)
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [roles, setRoles] = useState([])
  const [editId, setEditId] = useState(0)

  const [filter, setFilter] = useState({
    code: '',
    name: ''
  })

  const [debouncedCode] = useDebounce(filter.code, 1000)
  const [debouncedName] = useDebounce(filter.name, 1000)

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)
  const { per_page, current_page } = meta

  const prevCode = useRef(debouncedCode)
  const prevName = useRef(debouncedName)
  const abortControllerRef = useRef(null)

  useEffect(() => {
    const codeChanged = prevCode.current !== debouncedCode
    const nameChanged = prevName.current !== debouncedName

    if (!codeChanged && !nameChanged) {
      return
    }

    prevCode.current = debouncedCode
    prevName.current = debouncedName

    if (current_page !== 1) {
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    } else {
      loadRole()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCode, debouncedName])

  useEffect(() => {
    loadRole()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page])

  const loadRole = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const res = await api.get('/cms/role/list', {
        signal,
        params: {
          role_code: debouncedCode,
          role_name: debouncedName,
          per_page,
          page: current_page
        }
      })
      if (res.data.success) {
        const { metadata, data } = res.data
        setRoles(data)
        setMeta(metadata)
      }
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    setShowModalUpdate(true)
    setEditId(id)
  }

  const resetFilters = () => {
    setFilter({ code: '', name: '' })
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  return (
    <>
      <div className="container-fluid px-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Master Role</h3>
          </Col>
          <Col className="text-end">
            <Button
              className="btn btn-primary-orange shadow-sm rounded px-4"
              onClick={() => setShowModalAdd(true)}
            >
              <Plus size={16} className="me-2" />
              Add Role
            </Button>
          </Col>
        </Row>

        <Card style={cardFilterStyle}>
          <Row className="g-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label style={labelStyle}>Search Code</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#ffffff',
                      borderRight: 'none',
                      borderTopLeftRadius: '8px',
                      borderBottomLeftRadius: '8px',
                      borderColor: '#e9ecef'
                    }}
                  >
                    <Search size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Code..."
                    value={filter.code}
                    onChange={(e) =>
                      setFilter({ ...filter, code: e.target.value })
                    }
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: '0',
                      borderBottomLeftRadius: '0'
                    }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label style={labelStyle}>Search Name</Form.Label>
                <InputGroup>
                  <InputGroup.Text
                    style={{
                      backgroundColor: '#ffffff',
                      borderRight: 'none',
                      borderTopLeftRadius: '8px',
                      borderBottomLeftRadius: '8px',
                      borderColor: '#e9ecef'
                    }}
                  >
                    <Search size={16} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search Name..."
                    value={filter.name}
                    onChange={(e) =>
                      setFilter({ ...filter, name: e.target.value })
                    }
                    style={{
                      ...FormControlStyle,
                      borderLeft: 'none',
                      borderTopLeftRadius: '0',
                      borderBottomLeftRadius: '0'
                    }}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                variant="light"
                className="w-100 rounded shadow-sm"
                style={{
                  height: '45px',
                  borderColor: '#e9ecef',
                  color: '#6c757d',
                  fontWeight: '600'
                }}
                onClick={resetFilters}
              >
                <RotateCw size={16} className="me-2" />
                Reset
              </Button>
            </Col>
          </Row>
        </Card>

        <Row className="mt-2">
          <Col xs={12}>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <Table responsive hover className="mb-0 b-table-frameless">
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th className="p-2 align-middle">Role Code</th>
                    <th className="p-2 align-middle">Role Name</th>
                    <th className="p-2 align-middle text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="text-center p-5">
                        <Loader />
                      </td>
                    </tr>
                  )}
                  {!loading && roles.length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <RowEmpty colSpan={3} />
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    roles.length > 0 &&
                    roles.map((role) => {
                      return (
                        <tr key={role.id}>
                          <td
                            className="p-2 align-middle text-dark fw-medium"
                            style={tableStyle}
                          >
                            {role.role_code}
                          </td>
                          <td
                            className="p-2 align-middle text-muted"
                            style={tableStyle}
                          >
                            {role.role_name}
                          </td>
                          <td className="p-2 align-middle text-end">
                            <TableAction>
                              <button
                                className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                                style={{ width: '32px', height: '32px' }}
                                onClick={() => handleEdit(role.id)}
                              >
                                <Pencil size={13} />
                              </button>
                            </TableAction>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              </Table>
              {roles.length > 0 && (
                <div className="p-3 border-top bg-white">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      <ModalAddRole
        show={showModalAdd}
        onHide={() => setShowModalAdd(false)}
        refreshData={() => loadRole()}
      ></ModalAddRole>
      <ModalUpdateRole
        show={showModalUpdate}
        onHide={() => setShowModalUpdate(false)}
        id={editId}
        refreshData={() => loadRole()}
      ></ModalUpdateRole>
    </>
  )
}

export default MasterRolePage
